import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TONE = { ok: "text-emerald-700", applied: "text-emerald-700", failed: "text-red-700", running: "text-amber-700", rolled_back: "text-stone-600" };

function RunRow({ r }) {
  return (
    <li className="py-2 text-sm" data-testid={`fee-repair-run-${r.run_id}`}>
      <span className="font-mono text-xs">{r.run_id}</span>{" "}
      <span className="font-medium">{r.mode}</span>{" "}
      <span className={TONE[r.status] || ""}>{r.status}</span>{" "}
      <span className="text-xs text-stone-500">{(r.finished_at || r.created_at || r.started_at || "").slice(0, 16).replace("T", " ")}</span>
      {r.actions && <p className="text-xs text-stone-600 mt-0.5 break-words">{Object.entries(r.actions).map(([k, v]) => `${k}: ${v}`).join(" · ")} · reconciles: {String(r.reconciles)}</p>}
      {r.mode === "apply" && <p className="text-xs text-stone-600 mt-0.5">modified {r.modified ?? "…"} · conflicts {r.conflicts ?? "…"}{r.rollback_restored != null ? ` · rolled back ${r.rollback_restored}` : ""}</p>}
      {r.error && <p className="text-xs text-red-700 mt-0.5 break-words">{r.error}</p>}
    </li>
  );
}

function Confirm({ verb, onGo, disabled }) {
  const [id, setId] = useState("");
  const [text, setText] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <input value={id} onChange={(e) => setId(e.target.value.trim())} placeholder="run id" className="border border-stone-300 rounded px-2 py-1 text-xs font-mono w-72" data-testid={`fee-repair-${verb.toLowerCase()}-id`} />
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`${verb} <run id>`} className="border border-stone-300 rounded px-2 py-1 text-xs font-mono w-80" data-testid={`fee-repair-${verb.toLowerCase()}-confirm`} />
      <Button size="sm" variant="outline" disabled={disabled || !id || text !== `${verb} ${id}`} onClick={() => onGo(id, text)} data-testid={`fee-repair-${verb.toLowerCase()}-btn`}>{verb === "APPLY" ? "Apply reviewed dry-run" : "Roll back"}</Button>
    </div>
  );
}

const errText = (e) => {
  const s = e?.response?.status;
  const d = e?.response?.data?.detail;
  const msg = typeof d === "string" ? d : d ? JSON.stringify(d).slice(0, 300) : e?.message || "no response";
  return `${s ? `HTTP ${s}: ` : "Network error: "}${msg}`;
};

export function FeeRepairCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [pollUntil, setPollUntil] = useState(0);
  const load = useCallback(() => api.get("/admin/fee-repair").then(({ data }) => setData(data)).catch((e) => toast.error(`Could not load fee repair (${errText(e)})`)), []);
  useEffect(() => { load(); }, [load]);
  const running = data?.runs?.some((r) => r.status === "running");
  useEffect(() => {
    if (!running && Date.now() > pollUntil) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, pollUntil, load]);
  const post = async (path, body, msg) => {
    setBusy(true);
    const at = new Date().toISOString().slice(11, 19);
    try {
      await api.post(`/admin/fee-repair/${path}`, body);
      setLast({ ok: true, text: `${at} UTC · ${msg}. The server accepted the request; checking for a new run row every 5 seconds for 3 minutes.` });
      toast.success(msg);
      setPollUntil(Date.now() + 180000);
      load();
    } catch (e) {
      setLast({ ok: false, text: `${at} UTC · ${path} rejected · ${errText(e)}` });
      toast.error(errText(e));
    } finally { setBusy(false); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-stone-400 mt-8" data-testid="fee-repair-loading" />;
  return (
    <section className="mt-10 border border-stone-200 rounded-lg p-5 bg-white" data-testid="fee-repair-card">
      <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2"><DatabaseBackup className="h-5 w-5" /> US fee repair ({data.migration})</h2>
      <p className="text-sm text-stone-600 mt-1" data-testid="fee-repair-meta">
        Database <span className="font-mono">{data.db_name}</span> · Scorecard snapshot {data.snapshot.version} fetched {data.snapshot.fetched_at?.slice(0, 10)} ·
        Apply/rollback {data.apply_enabled ? <strong className="text-amber-800">enabled on this server</strong> : "disabled on this server"}
      </p>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => post("dry-run", {}, "Dry-run started; it appears below in a few minutes")} data-testid="fee-repair-dry-run-btn">Run dry-run (no changes)</Button>
      <Confirm verb="APPLY" disabled={busy || running || !data.apply_enabled} onGo={(run_id, confirm) => post("apply", { run_id, confirm }, "Apply request accepted")} />
      <Confirm verb="ROLLBACK" disabled={busy || running || !data.apply_enabled} onGo={(run_id, confirm) => post("rollback", { run_id, confirm }, "Rollback request accepted")} />
      <Button size="sm" variant="ghost" className="mt-2" onClick={load} data-testid="fee-repair-refresh-btn">Refresh</Button>
      {busy && <p className="text-xs text-stone-500 mt-2" data-testid="fee-repair-busy">Sending request…</p>}
      {last && <p className={`text-xs mt-2 break-words ${last.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="fee-repair-last-result">{last.text}</p>}
      {data.runs.length ? <ul className="mt-3 divide-y divide-stone-200" data-testid="fee-repair-runs">{data.runs.map((r) => <RunRow key={r.run_id} r={r} />)}</ul>
        : <p className="text-xs text-stone-500 mt-3" data-testid="fee-repair-no-runs">No runs yet.</p>}
    </section>
  );
}
